import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Save, 
  Play, 
  Settings, 
  Zap, 
  Plus, 
  Minus, 
  RotateCcw, 
  Download, 
  Upload, 
  Eye, 
  Bug, 
  X,
  ArrowLeft,
  Grid,
  Move,
  MousePointer,
  Link,
  Trash2,
  Copy,
  Edit,
  Undo2,
  Redo2,
  RefreshCw
} from 'lucide-react';
import { WorkflowCanvas } from './WorkflowCanvas';
import { StepPalette } from './StepPalette';
import { StepPropertiesPanel } from './StepPropertiesPanel';
import { WorkflowSettings } from './WorkflowSettings';
import { WorkflowTestPanel } from './WorkflowTestPanel';
import { AIWorkflowBuilder } from './AIWorkflowBuilder';
import { ConfirmationDialog } from './ConfirmationDialog';
import { Workflow, WorkflowStep, WorkflowConnection, WorkflowBuilderState, BuilderMode } from '../types/workflow';
import { workflowService } from '../services/workflowService';
import { useToast } from '../../../components/ui/Toast';

interface WorkflowBuilderProps {
  workflow?: Workflow | null;
  onClose: () => void;
  onSave: (workflow: Workflow) => void;
}

export const WorkflowBuilder: React.FC<WorkflowBuilderProps> = ({
  workflow: initialWorkflow,
  onClose,
  onSave
}) => {
  const { addToast } = useToast();
  const [builderState, setBuilderState] = useState<WorkflowBuilderState>({
    workflow: initialWorkflow || createEmptyWorkflow(),
    zoom: 1,
    pan: { x: 0, y: 0 },
    mode: 'select'
  });

  const [showSettings, setShowSettings] = useState(false);
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [showStepProperties, setShowStepProperties] = useState(false);
  const [showAIBuilder, setShowAIBuilder] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Undo functionality
  const [undoStack, setUndoStack] = useState<Workflow[]>([]);
  const [redoStack, setRedoStack] = useState<Workflow[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const canvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Save current state to undo stack before making changes
  const saveToUndoStack = useCallback((currentWorkflow: Workflow) => {
    setUndoStack(prev => {
      const newStack = [...prev, JSON.parse(JSON.stringify(currentWorkflow))];
      // Keep only last 20 states to prevent memory issues
      return newStack.slice(-20);
    });
    setCanUndo(true);
    
    // Clear redo stack when new action is performed
    setRedoStack([]);
    setCanRedo(false);
  }, []);

  // Undo last action
  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    
    const previousState = undoStack[undoStack.length - 1];
    
    // Save current state to redo stack
    setRedoStack(prev => [...prev, JSON.parse(JSON.stringify(builderState.workflow))]);
    setCanRedo(true);
    
    setBuilderState(prev => ({
      ...prev,
      workflow: previousState,
      selectedStep: undefined,
      selectedConnection: undefined
    }));
    
    setUndoStack(prev => prev.slice(0, -1));
    setCanUndo(undoStack.length > 1);
    setHasUnsavedChanges(true);
    
    addToast({
      title: 'Action Undone',
      message: 'Last action has been undone',
      type: 'info'
    });
  }, [undoStack, builderState.workflow, addToast]);

  // Redo last undone action
  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    
    const nextState = redoStack[redoStack.length - 1];
    
    // Save current state to undo stack
    setUndoStack(prev => [...prev, JSON.parse(JSON.stringify(builderState.workflow))]);
    setCanUndo(true);
    
    setBuilderState(prev => ({
      ...prev,
      workflow: nextState,
      selectedStep: undefined,
      selectedConnection: undefined
    }));
    
    setRedoStack(prev => prev.slice(0, -1));
    setCanRedo(redoStack.length > 1);
    setHasUnsavedChanges(true);
    
    addToast({
      title: 'Action Redone',
      message: 'Last undone action has been redone',
      type: 'info'
    });
  }, [redoStack, builderState.workflow, addToast]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey) {
        if (e.key === 'z' && !e.shiftKey) {
          e.preventDefault();
          handleUndo();
        } else if ((e.key === 'y') || (e.key === 'z' && e.shiftKey)) {
          e.preventDefault();
          handleRedo();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Reset workflow to empty state
  const handleResetWorkflow = useCallback(() => {
    setConfirmDialog({
      isOpen: true,
      title: 'Reset Workflow',
      message: 'Are you sure you want to reset the workflow to an empty state? This action cannot be undone and will remove all steps and connections.',
      variant: 'warning',
      onConfirm: () => {
        saveToUndoStack(builderState.workflow);
        const emptyWorkflow = createEmptyWorkflow();
        setBuilderState(prev => ({
          ...prev,
          workflow: emptyWorkflow,
          selectedStep: undefined,
          selectedConnection: undefined
        }));
        setHasUnsavedChanges(true);
        
        addToast({
          title: 'Workflow Reset',
          message: 'Workflow has been reset to empty state',
          type: 'info'
        });
        
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      }
    });
  }, [builderState.workflow, saveToUndoStack, addToast]);

  const updateWorkflow = useCallback((updates: Partial<Workflow>) => {
    setBuilderState(prev => ({
      ...prev,
      workflow: { ...prev.workflow, ...updates }
    }));
    setHasUnsavedChanges(true);
  }, []);

  const updateStep = useCallback((stepId: string, updates: Partial<WorkflowStep>) => {
    setBuilderState(prev => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        definition: {
          ...prev.workflow.definition,
          steps: prev.workflow.definition.steps.map(step =>
            step.id === stepId ? { ...step, ...updates } : step
          )
        }
      }
    }));
    setHasUnsavedChanges(true);
  }, []);

  const addStep = useCallback((step: WorkflowStep) => {
    saveToUndoStack(builderState.workflow);
    setBuilderState(prev => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        definition: {
          ...prev.workflow.definition,
          steps: [...prev.workflow.definition.steps, step]
        }
      }
    }));
    setHasUnsavedChanges(true);
  }, [builderState.workflow, saveToUndoStack]);

  const removeStep = useCallback((stepId: string) => {
    saveToUndoStack(builderState.workflow);
    setBuilderState(prev => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        definition: {
          ...prev.workflow.definition,
          steps: prev.workflow.definition.steps.filter(step => step.id !== stepId)
        }
      },
      selectedStep: prev.selectedStep?.id === stepId ? undefined : prev.selectedStep
    }));
    setHasUnsavedChanges(true);
  }, [builderState.workflow, saveToUndoStack]);

  const addConnection = useCallback((connection: WorkflowConnection) => {
    console.log('Adding connection:', connection);
    saveToUndoStack(builderState.workflow);
    setBuilderState(prev => {
      const sourceStep = prev.workflow.definition.steps.find(s => s.id === connection.sourceStepId);
      if (!sourceStep) {
        console.log('Source step not found:', connection.sourceStepId);
        return prev;
      }

      console.log('Source step found:', sourceStep);
      const updatedSteps = prev.workflow.definition.steps.map(step =>
        step.id === connection.sourceStepId
          ? { ...step, connections: [...step.connections, connection] }
          : step
      );
      
      console.log('Updated steps:', updatedSteps);
      
      return {
        ...prev,
        workflow: {
          ...prev.workflow,
          definition: {
            ...prev.workflow.definition,
            steps: updatedSteps
          }
        }
      };
    });
    setHasUnsavedChanges(true);
  }, [builderState.workflow, saveToUndoStack]);

  const removeConnection = useCallback((connectionId: string) => {
    saveToUndoStack(builderState.workflow);
    setBuilderState(prev => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        definition: {
          ...prev.workflow.definition,
          steps: prev.workflow.definition.steps.map(step => ({
            ...step,
            connections: step.connections.filter(conn => conn.id !== connectionId)
          }))
        }
      }
    }));
    setHasUnsavedChanges(true);
  }, [builderState.workflow, saveToUndoStack]);

  const selectStep = useCallback((step: WorkflowStep | undefined) => {
    setBuilderState(prev => ({ ...prev, selectedStep: step }));
    setShowStepProperties(!!step);
  }, []);

  const selectConnection = useCallback((connection: WorkflowConnection | undefined) => {
    setBuilderState(prev => ({ ...prev, selectedConnection: connection }));
  }, []);

  const setMode = useCallback((mode: BuilderMode) => {
    setBuilderState(prev => ({ ...prev, mode }));
  }, []);

  const setZoom = useCallback((zoom: number) => {
    setBuilderState(prev => ({ ...prev, zoom: Math.max(0.1, Math.min(3, zoom)) }));
  }, []);

  const setPan = useCallback((pan: { x: number; y: number }) => {
    setBuilderState(prev => ({ ...prev, pan }));
  }, []);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Validate workflow
      const validation = await workflowService.validateWorkflow(builderState.workflow);
      if (!validation.isValid) {
        setValidationErrors(validation.errors || []);
        return;
      }

      let savedWorkflow: Workflow;
      if (builderState.workflow.id) {
        savedWorkflow = await workflowService.updateWorkflow(builderState.workflow.id, {
          name: builderState.workflow.name,
          description: builderState.workflow.description,
          definition: builderState.workflow.definition,
          triggers: builderState.workflow.triggers,
          isActive: builderState.workflow.isActive
        });
      } else {
        savedWorkflow = await workflowService.createWorkflow({
          name: builderState.workflow.name,
          description: builderState.workflow.description,
          definition: builderState.workflow.definition,
          triggers: builderState.workflow.triggers,
          isActive: builderState.workflow.isActive
        });
      }

      setBuilderState(prev => ({ ...prev, workflow: savedWorkflow }));
      setHasUnsavedChanges(false);
      setValidationErrors([]);
      addToast({
        title: 'Workflow Saved',
        message: 'The workflow was saved successfully',
        type: 'success'
      });
      onSave(savedWorkflow);
    } catch (error) {
      console.error('Failed to save workflow:', error);
      addToast({
        title: 'Save Error',
        message: 'Failed to save workflow. Please try again.',
        type: 'error'
      });
      setValidationErrors(['Failed to save workflow. Please try again.']);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setIsExecuting(true);
      await workflowService.testWorkflow(builderState.workflow.id);
      setShowTestPanel(true);
    } catch (error) {
      console.error('Failed to test workflow:', error);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleExecute = async () => {
    try {
      setIsExecuting(true);
      // Execute in test mode to allow draft workflows to run
      await workflowService.executeWorkflow(builderState.workflow.id, {
        testMode: true,
        debugMode: false
      });
      addToast({
        title: 'Workflow Executed',
        message: 'The workflow was executed successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to execute workflow:', error);
      addToast({
        title: 'Workflow Execution Error',
        message: 'An error occurred while executing the workflow',
        type: 'error'
      });
    } finally {
      setIsExecuting(false);
    }
  };

  const handlePreview = async () => {
    try {
      const preview = await workflowService.previewWorkflow(builderState.workflow);
      console.log('Workflow preview:', preview);
    } catch (error) {
      console.error('Failed to preview workflow:', error);
    }
  };

  const handleExport = async () => {
    if (!builderState.workflow.id) return;
    
    try {
      const blob = await workflowService.exportWorkflow(builderState.workflow.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${builderState.workflow.name}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export workflow:', error);
    }
  };

  const handleImport = async (file: File) => {
    try {
      const importedWorkflow = await workflowService.importWorkflow(file);
      setBuilderState(prev => ({ ...prev, workflow: importedWorkflow }));
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error('Failed to import workflow:', error);
    }
  };

  const resetCanvas = () => {
    setBuilderState(prev => ({
      ...prev,
      zoom: 1,
      pan: { x: 0, y: 0 },
      selectedStep: undefined,
      selectedConnection: undefined
    }));
  };

  return (
    <div className="fixed inset-0 bg-neutral-950 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-neutral-900/50 backdrop-blur-sm border-b border-neutral-800 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 text-neutral-400 hover:text-white rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div>
              <input
                type="text"
                value={builderState.workflow.name}
                onChange={(e) => updateWorkflow({ name: e.target.value })}
                className="text-xl font-bold bg-transparent text-white border-none outline-none focus:bg-neutral-800 rounded px-2 py-1"
                placeholder="Workflow Name"
              />
              <p className="text-sm text-neutral-400">
                {builderState.workflow.definition.steps.length} steps
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Mode Toggle */}
            <div className="flex items-center gap-1 bg-neutral-800 rounded-lg p-1">
              <button
                onClick={() => setMode('select')}
                className={`p-2 rounded transition-colors ${
                  builderState.mode === 'select' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-neutral-400 hover:text-white'
                }`}
                title="Select mode"
              >
                <MousePointer className="w-4 h-4" />
              </button>
              <button
                onClick={() => setMode('connect')}
                className={`p-2 rounded transition-colors ${
                  builderState.mode === 'connect' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-neutral-400 hover:text-white'
                }`}
                title="Connect mode"
              >
                <Link className="w-4 h-4" />
              </button>
              <button
                onClick={() => setMode('pan')}
                className={`p-2 rounded transition-colors ${
                  builderState.mode === 'pan' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-neutral-400 hover:text-white'
                }`}
                title="Pan mode"
              >
                <Move className="w-4 h-4" />
              </button>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-1 bg-neutral-800 rounded-lg p-1">
              <button
                onClick={() => setZoom(builderState.zoom - 0.1)}
                className="p-2 text-neutral-400 hover:text-white rounded transition-colors"
                title="Zoom out"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="px-2 text-sm text-neutral-300 min-w-[3rem] text-center">
                {Math.round(builderState.zoom * 100)}%
              </span>
              <button
                onClick={() => setZoom(builderState.zoom + 0.1)}
                className="p-2 text-neutral-400 hover:text-white rounded transition-colors"
                title="Zoom in"
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                onClick={resetCanvas}
                className="p-2 text-neutral-400 hover:text-white rounded transition-colors"
                title="Reset view"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>

            {/* Undo, Redo and Reset Controls */}
            <div className="flex items-center gap-1 bg-neutral-800 rounded-lg p-1">
              <button
                onClick={handleUndo}
                disabled={!canUndo}
                className="p-2 text-neutral-400 hover:text-white disabled:text-neutral-600 disabled:cursor-not-allowed rounded transition-colors"
                title="Undo last action (Ctrl+Z)"
              >
                <Undo2 className="w-4 h-4" />
              </button>
              <button
                onClick={handleRedo}
                disabled={!canRedo}
                className="p-2 text-neutral-400 hover:text-white disabled:text-neutral-600 disabled:cursor-not-allowed rounded transition-colors"
                title="Redo last action (Ctrl+Y)"
              >
                <Redo2 className="w-4 h-4" />
              </button>
              <button
                onClick={handleResetWorkflow}
                className="p-2 text-neutral-400 hover:text-red-400 rounded transition-colors"
                title="Reset workflow"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAIBuilder(true)}
                className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-colors"
              >
                <Zap className="w-4 h-4" />
                AI Builder
              </button>
              
              <button
                onClick={handlePreview}
                className="flex items-center gap-2 px-3 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg transition-colors"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
              
              <button
                onClick={handleTest}
                disabled={isExecuting || !builderState.workflow.id}
                className="flex items-center gap-2 px-3 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-neutral-700 disabled:text-neutral-400 text-white rounded-lg transition-colors"
              >
                <Bug className="w-4 h-4" />
                Test
              </button>
              
              <button
                onClick={handleExecute}
                disabled={isExecuting || !builderState.workflow.id}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-neutral-700 disabled:text-neutral-400 text-white rounded-lg transition-colors"
              >
                <Play className="w-4 h-4" />
                {isExecuting ? 'Running...' : 'Execute'}
              </button>
              
              <button
                onClick={handleSave}
                disabled={isSaving || !hasUnsavedChanges}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-neutral-700 disabled:text-neutral-400 text-white rounded-lg transition-colors"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>

            {/* Settings */}
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 text-neutral-400 hover:text-white rounded-lg transition-colors"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="mt-4 p-3 bg-red-900/20 border border-red-800 rounded-lg">
            <div className="flex items-center gap-2 text-red-400 mb-2">
              <X className="w-4 h-4" />
              <span className="font-medium">Validation Errors</span>
            </div>
            <ul className="text-sm text-red-300 space-y-1">
              {validationErrors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Step Palette */}
        <div className="w-80 bg-neutral-900/30 border-r border-neutral-800 overflow-y-auto">
          <StepPalette
            onStepDragStart={(step) => console.log('Drag started:', step)}
            onStepDragEnd={() => console.log('Drag ended')}
          />
        </div>

        {/* Canvas */}
        <div className="flex-1 relative" ref={canvasRef}>
          <WorkflowCanvas
            workflow={builderState.workflow}
            selectedStep={builderState.selectedStep}
            selectedConnection={builderState.selectedConnection}
            mode={builderState.mode}
            zoom={builderState.zoom}
            pan={builderState.pan}
            onStepSelect={selectStep}
            onStepAdd={addStep}
            onStepUpdate={updateStep}
            onStepDelete={removeStep}
            onConnectionSelect={selectConnection}
            onConnectionAdd={addConnection}
            onConnectionDelete={removeConnection}
            onZoomChange={setZoom}
            onPanChange={setPan}
          />
        </div>

        {/* Properties Panel */}
        {showStepProperties && builderState.selectedStep && (
          <div className="w-96 bg-neutral-900/30 border-l border-neutral-800 overflow-y-auto">
            <StepPropertiesPanel
              step={builderState.selectedStep}
              onUpdate={(updates: Partial<WorkflowStep>) => updateStep(builderState.selectedStep!.id, updates)}
              onClose={() => setShowStepProperties(false)}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      {showAIBuilder && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-neutral-900 rounded-xl border border-neutral-800 w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <AIWorkflowBuilder
              workflow={builderState.workflow}
              onClose={() => setShowAIBuilder(false)}
              onSave={(generatedWorkflow: Workflow) => {
                // Update the current workflow with generated content
                updateWorkflow(generatedWorkflow);
                setShowAIBuilder(false);
                addToast({
                  title: 'Workflow Generated',
                  message: `Successfully generated workflow with ${generatedWorkflow.definition.steps.length} steps`,
                  type: 'success'
                });
              }}
            />
          </div>
        </div>
      )}
      
      {showSettings && (
        <WorkflowSettings
          workflow={builderState.workflow}
          onUpdate={updateWorkflow}
          onClose={() => setShowSettings(false)}
        />
      )}

      {showTestPanel && (
        <WorkflowTestPanel
          workflow={builderState.workflow}
          onClose={() => setShowTestPanel(false)}
        />
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

function createEmptyWorkflow(): Workflow {
  return {
    id: '',
    name: 'New Workflow',
    description: '',
    definition: {
      steps: [],
      variables: [],
      settings: {}
    },
    triggers: [],
    isActive: false,
    status: 'draft',
    createdBy: '',
    createdAt: new Date(),
    updatedAt: new Date(),
    executionCount: 0,
    successRate: 0
  };
} 